/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/ui/utils/tailwind";
import { maskPhone, onlyLetters } from "../utils/masks";
import {
  getReferralNameError,
  getReferralPhoneError,
  type ReferralFieldError,
} from "../utils/referral-form-validation";
import { Form, Label, Text, TextInput, UnstyledButton } from '@/shared/ui';

interface Props {
  disabled?: boolean;
  onSubmit: (payload: { referred_name: string; referred_phone: string }) => Promise<void>;
  onCancel?: () => void;
}

const fieldClass = cn(
  "w-full px-[var(--space-3)] py-[var(--space-2)]",
  "text-[length:var(--text-body-sm)] [font-family:var(--font-family)]",
  "border border-[var(--border-subtle)] rounded-(--radius-md)",
  "bg-[var(--surface-primary)] text-[var(--text-primary)]",
);

const ERROR_KEYS: Record<ReferralFieldError, string> = {
  nameRequired: "detail.referralNameRequired",
  nameTooShort: "detail.referralNameTooShort",
  phoneRequired: "detail.referralPhoneRequired",
  phoneInvalid: "detail.referralPhoneInvalid",
};

/**
 * Inline form to register a new client indication (name + phone).
 */
export function ReferralAddForm({ disabled, onSubmit, onCancel }: Props) {
  const { t } = useTranslation("features/clients");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [nameError, setNameError] = useState<ReferralFieldError | null>(null);
  const [phoneError, setPhoneError] = useState<ReferralFieldError | null>(null);
  const [saving, setSaving] = useState(false);

  const handleNameChange = (value: string) => {
    setName(onlyLetters(value));
    setNameError(null);
  };

  const handlePhoneChange = (value: string) => {
    setPhone(maskPhone(value));
    setPhoneError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    const nextNameError = getReferralNameError(name);
    const nextPhoneError = getReferralPhoneError(phone);
    setNameError(nextNameError);
    setPhoneError(nextPhoneError);
    if (nextNameError || nextPhoneError) return;
    setSaving(true);
    try {
      await onSubmit({ referred_name: name.trim(), referred_phone: phone });
      setName("");
      setPhone("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form
      data-testid="referral-add-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-[var(--space-3)] mb-[var(--space-3)] p-[var(--space-3)] rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)]"
      noValidate
    >
      <Label className="flex flex-col gap-[var(--space-1)] text-[length:var(--text-body-sm)] text-[var(--text-secondary)]">
        {t("detail.referredName", "Name")}
        <TextInput
          aria-label={t("detail.referredName", "Name")}
          aria-invalid={Boolean(nameError)}
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          className={cn(fieldClass, nameError && "border-[var(--status-error)]")}
          disabled={disabled || saving}
          autoComplete="off"
          placeholder={t("detail.referredNamePlaceholder", "Full name")}
        />
        {nameError && (
          <Text variant="bodySm" component="span" role="alert" className="text-[var(--status-error)]">
            {t(ERROR_KEYS[nameError])}
          </Text>
        )}
      </Label>
      <Label className="flex flex-col gap-[var(--space-1)] text-[length:var(--text-body-sm)] text-[var(--text-secondary)]">
        {t("detail.referredPhone", "Phone")}
        <TextInput
          aria-label={t("detail.referredPhone", "Phone")}
          aria-invalid={Boolean(phoneError)}
          inputMode="numeric"
          value={phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          className={cn(fieldClass, phoneError && "border-[var(--status-error)]")}
          disabled={disabled || saving}
          autoComplete="off"
          placeholder={t("detail.referredPhonePlaceholder", "(11) 99999-9999")}
        />
        {phoneError && (
          <Text variant="bodySm" component="span" role="alert" className="text-[var(--status-error)]">
            {t(ERROR_KEYS[phoneError])}
          </Text>
        )}
      </Label>
      <div className="flex items-center gap-[var(--space-2)]">
        <UnstyledButton
          type="submit"
          disabled={disabled || saving}
          className={cn(
            "px-[var(--space-3)] py-[var(--space-2)]",
            "text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)]",
            "rounded-(--radius-md) border-none cursor-pointer",
            "bg-[var(--seed-base)] text-(--text-inverse)",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          {t("detail.addReferral", "Add")}
        </UnstyledButton>
        {onCancel && (
          <UnstyledButton
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-body-sm)] text-[var(--text-secondary)] bg-transparent border-none cursor-pointer hover:underline"
          >
            {t("detail.cancelReferral", "Cancel")}
          </UnstyledButton>
        )}
      </div>
    </Form>
  );
}
